import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// shadcn/ui Imports
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Lucide Icons
import { MapPin, Mail as MailIcon, Phone as PhoneIcon, Clock } from 'lucide-react';

// Actual Form Component
import ContactFormComponent from '../../../forms/contact/contact.form';

import { cn } from '@/lib/utils';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    } 
  }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function ContactFormSection({ viewport, isRTL }) {
  const { t } = useTranslation();
  const { isMobile = false } = viewport || {};

  const contactInfo = [
    {
      icon: MapPin,
      title: t('homepage.contact.address.title', 'Our Office'),
      lines: [t('homepage.contact.address.content', 'Al Saqr HQ, Algiers, Algeria')],
      color: 'text-blue-500'
    },
    {
      icon: MailIcon,
      title: t('homepage.contact.email.title', 'Email Us'),
      lines: [t('homepage.contact.email.content', 'contact@alsaqr.io')],
      href: 'mailto:contact@alsaqr.io',
      color: 'text-red-500'
    },
    {
      icon: PhoneIcon,
      title: t('homepage.contact.phone.title', 'Call Us'),
      lines: [t('homepage.contact.phone.content', '+213 698 30 44 50')],
      href: 'tel:+213698304450',
      color: 'text-green-500'
    },
    {
      icon: Clock,
      title: t('homepage.contact.hours.title', 'Office Hours'),
      lines: [
        t('homepage.contact.hours.days', 'Sunday - Thursday'),
        t('homepage.contact.hours.time', '9:00 AM - 5:00 PM (GMT+1)')
      ],
      color: 'text-purple-500'
    }
  ];

  return (
    <section 
      id="contact" 
      className="relative py-20 md:py-28 lg:py-32 bg-muted/20 overflow-hidden"
    >
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-primary/5 rounded-full filter blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-secondary/5 rounded-full filter blur-3xl opacity-50 pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerChildren}
          className="text-center mb-12 md:mb-16 max-w-2xl mx-auto"
        >
          <motion.div variants={fadeInUp}>
             <Badge
              variant="outline"
              className="mb-3 px-3 py-1 text-xs font-medium rounded-full border-primary/30 bg-primary/10 text-primary"
            >
              {t('homepage.contact.badge', 'Connect with Al Saqr Team')}
            </Badge>
          </motion.div>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground">
            {t('homepage.contact.title', 'Get in Touch About Al Saqr IoT Platform')}
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-md md:text-lg text-muted-foreground max-w-xl mx-auto">
            {t('homepage.contact.subtitle', 'Questions about our IoT platform for fleet, edge, or gateways? Contact us for info or a personalized Al Saqr demo.')}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <motion.div 
            className="lg:col-span-4 space-y-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerChildren}
          >
            {contactInfo.map((item, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="p-6 bg-background shadow-lg hover:shadow-xl transition-shadow duration-300 rounded-lg">
                  <div className="flex items-start space-x-4" dir={isRTL ? "rtl" : "ltr"}>
                    <div className={cn("flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-muted", item.color, isRTL ? "ml-4" : "mr-4")}>
                      <item.icon size={20} className="text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">{item.title}</h3>
                      {item.href ? (
                        <a 
                          href={item.href} 
                          className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {item.lines.join(', ')}
                        </a>
                      ) : (
                        item.lines.map((line, i) => (
                          <p key={i} className="text-sm text-muted-foreground">{line}</p>
                        ))
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="lg:col-span-8"
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Card className="p-6 sm:p-8 md:p-10 bg-background shadow-xl rounded-xl">
              <ContactFormComponent />
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 